import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HttpApis from "../../services/HttpApis";
import Footer from "../Footer/Footer";
import { SkeletonBlock } from "../loader/Skeleton";
import { LEGAL_DOCS, isExternalUrl } from "./legalLinks";
import { useTenant } from "../../TenantContext";
import "./LegalPage.css";

const formatEffectiveDate = (value) => {
  if (!value) return null;
  // "YYYY-MM-DD" → local calendar date (no timezone shift).
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * One page for all three legal documents. Content comes from the CMS via
 * GET /v1/config/legal/:type (published only; the backend sanitizes the HTML
 * on save with an allow-list). External-URL documents redirect there.
 */
const LegalPage = ({ type }) => {
  const doc = LEGAL_DOCS.find((d) => d.type === type);
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", data: null });
    HttpApis.getLegalDocument(type)
      .then((response) => {
        if (cancelled) return;
        const data = response?.data?.data;
        if (!data) return setState({ status: "unavailable", data: null });
        if (data.mode === "external_url") {
          if (isExternalUrl(data.external_url)) {
            window.location.replace(data.external_url);
            return setState({ status: "redirecting", data });
          }
          return setState({ status: "unavailable", data: null });
        }
        setState({ status: "ready", data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "unavailable", data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [type]);

  const { status, data } = state;
  // Tenant logo chain (tenant logo -> env logo -> bundled default), as the
  // footer and the rest of the app use; upstream hardcoded the env logo.
  const { tenant, logo } = useTenant();
  const title = data?.title || doc?.label || "Legal";
  const effective = formatEffectiveDate(data?.effective_date);
  // A designed page brings its own <h1> header: show it full width, as
  // written, instead of the default title and narrow column.
  const ownHeader = /<h1[\s>]/i.test(data?.content_html || "");

  useEffect(() => {
    if (status === "ready") document.title = title;
  }, [status, title]);

  return (
    <div className="legal-page">
      <div className="legal-page__logo-bar">
        <Link to="/home">
          <img
            src={logo}
            alt={tenant?.name || "Logo"}
            className="legal-page__logo"
          />
        </Link>
      </div>

      <main className={`legal-page__main${status === "ready" && ownHeader ? " legal-page__main--wide" : ""}`}>
        {status === "loading" && (
          <div className="legal-page__skeleton" data-testid="legal-skeleton" aria-busy="true">
            <SkeletonBlock width="45%" height={30} radius={6} style={{ marginBottom: 14 }} />
            <SkeletonBlock width="25%" height={12} radius={4} style={{ marginBottom: 28 }} />
            {[92, 100, 86, 97, 74, 100, 64].map((w, i) => (
              <SkeletonBlock key={i} width={`${w}%`} height={12} radius={4} style={{ marginBottom: 10 }} />
            ))}
          </div>
        )}

        {status === "redirecting" && (
          <p className="legal-page__notice">
            Opening {title}…{" "}
            <a href={data.external_url} rel="noopener noreferrer">
              Continue
            </a>
          </p>
        )}

        {status === "unavailable" && (
          <div className="legal-page__empty">
            <h1 className="legal-page__title">{doc?.label || "Page"}</h1>
            <p className="legal-page__notice">This page isn't available right now.</p>
            <Link className="legal-page__home" to="/home">
              Back to Home
            </Link>
          </div>
        )}

        {status === "ready" && (
          <article>
            {!ownHeader && <h1 className="legal-page__title">{title}</h1>}
            {!ownHeader && effective && <p className="legal-page__meta">Effective {effective}</p>}
            <div
              className="legal-page__content"
              // Sanitized server-side on save (allow-list: headings, p, lists,
              // links, tables, div/span, filtered inline styles). Never
              // user-supplied in the browser.
              dangerouslySetInnerHTML={{ __html: data.content_html || "" }}
            />
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default LegalPage;
